import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Icon } from 'ui';
import moment from 'utils/cops-moment';
import { ItemTypes, laneConfig } from '../../../config';
import { viewIds } from '../../../config/view';
import getDayHourOfDroppedBlock from '../../../engine/dropped-engine';
import { calculateSnap } from '../../../utils/date-functions';
import BlockDragPreview from '../../block/components/block-drag-preview';

const DragLayerWrapper = styled.div`
  position: absolute;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const StyledIcon = styled(Icon)`
  color: white !important;
`;

/**
 *
 * @param {top} number - top position of the gantt relative to browser window
 * @param {left} number - left position of the gantt relative to browser window
 */
const DndDragLayer = ({
  top,
  left,
  ganttDimensions,
  viewDimensions,
  getDimensions,
  selectedIds,
  view,
  dateReference,
}) => {
  // caption indication, no use of useState as this causes to many rerender loops
  let caption = '';
  const [dayBreakerBoundaries, setDayBreakerBoundaries] = useState([]);
  const { adjacentBlock } = useSelector(state => state.gantt);
  const { itemType, isDragging, currentOffset } = useDragLayer(monitor => ({
    itemType: monitor.getItemType(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  useEffect(() => {
    if (isDragging) {
      getDimensions();
    }
  }, [isDragging]);

  const calculateSnapping = useMemo(() => {
    if (ganttDimensions && viewDimensions) {
      const { widthGantt, widthDimension } = ganttDimensions;
      const { amountOfDays, amountOfHours, endHour, startHour } = viewDimensions;
      const amountDayBreakers = amountOfDays - 1;
      // eslint-disable-next-line
      const widthDay = (widthGantt - view?.parameters?.widthDayBreaker * amountDayBreakers) / amountOfDays;
      const snapToMinutes = calculateSnap(widthDay, endHour - startHour);

      const widthSnap = (snapToMinutes * widthDimension * widthGantt) / 100;
      // eslint-disable-next-line
      const maxSnapsInADay = (amountOfHours * 60) / snapToMinutes + 1;
      // eslint-disable-next-line
      const timePerSnap = amountOfHours / (maxSnapsInADay - 1);

      if (amountDayBreakers) {
        setDayBreakerBoundaries(
          Array(amountDayBreakers < 0 ? 0 : amountDayBreakers)
            .fill()
            ?.map((value, index) => ({
              start:
                // eslint-disable-next-line
                widthDay * (index + 1) +
                // eslint-disable-next-line
                view?.parameters?.widthDayBreaker * index +
                laneConfig.widthFirstColumn,
              end:
                // eslint-disable-next-line
                widthDay * (index + 1) +
                // eslint-disable-next-line
                view?.parameters?.widthDayBreaker * (index + 1) +
                laneConfig.widthFirstColumn,
            })),
        );
      }

      return {
        widthSnap,
        maxSnapsInADay,
        timePerSnap,
      };
    }
    return null;
  }, [ganttDimensions, viewDimensions]);

  const getPreviewCaption = (droppedLeftPx) => {
    const { id } = view;

    if (adjacentBlock && itemType !== ItemTypes.NON_PLANNING_BLOCK) {
      return <StyledIcon className='fa-solid fa-left-long-to-line' />;
    }
    const date = getDayHourOfDroppedBlock({
      droppedLeftPx,
      ganttDimensions,
      viewDimensions,
      dateReference,
      view,
    });

    if (id === viewIds.month) {
      return moment(date).format('DD/MM/YYYY');
    }
    return moment(date).format('HH:mm');
  };

  const renderItem = () => {
    switch (itemType) {
      case ItemTypes.BLOCK:
      case ItemTypes.ORDER:
      case ItemTypes.PLANNING:
      case ItemTypes.NON_PLANNING_BLOCK:
        return <BlockDragPreview caption={caption} type={itemType} selectedIds={selectedIds} />;
      default:
        return null;
    }
  };

  // using function to get style instead of styled-component because of performance issues
  const dragStyle = (offset) => {
    let extraStyles = {};

    if (!offset) {
      return {
        display: 'none',
      };
    }

    let { x, y } = offset;
    let leftOffset = left;

    // check if over daybreaker
    let overDay = 0;
    let overDayBeaker = false;
    for (let i = 0; i < dayBreakerBoundaries.length; i += 1) {
      const dayBreakerBoundary = dayBreakerBoundaries[i];
      if (i && x > dayBreakerBoundaries[i - 1].end && x < dayBreakerBoundary.start) {
        overDay = i;
      } else if (x > dayBreakerBoundaries[dayBreakerBoundaries.length - 1].end) {
        overDay = dayBreakerBoundaries.length;
      }
      if (x > dayBreakerBoundary.start && x < dayBreakerBoundary.end) {
        overDayBeaker = true;
        extraStyles = {
          opacity: 0.5,
        };
        break;
      }
    }

    // snap
    if (laneConfig.doSnap) {
      const { widthSnap } = calculateSnapping;
      if (!overDayBeaker) {
        let snapPositionX = x;
        if (snapPositionX <= leftOffset) {
          snapPositionX = leftOffset;
          extraStyles = {
            opacity: 0.5,
          };
        } else {
          let snaps = (x - leftOffset) / widthSnap;
          // time calculation
          const droppedLeftPx = x - leftOffset;
          caption = getPreviewCaption(droppedLeftPx);

          if (overDay) {
            leftOffset = dayBreakerBoundaries[overDay - 1].end;
            snaps = (x - leftOffset) / widthSnap;
          }

          snapPositionX = leftOffset + (snaps * widthSnap);
          // If adjacent, snapposition is left + duration - travelFrom (since we snap to the labour)
          // * the dimension in pixels + daybreakers + weekOffset
          if (adjacentBlock && itemType !== ItemTypes.NON_PLANNING_BLOCK) {
            const {
              nextWeekOffsetPx,
              widthDayBreaker,
              durationMinutes,
              travelFromWidth,
              durationTravelFromInMinutes,
              leftMinutes,
            } = adjacentBlock;
            const { widthDimensionPx } = ganttDimensions;
            snapPositionX = left +
              (nextWeekOffsetPx || 0) +
              (widthDayBreaker * overDay) +
              ((
                durationMinutes
                // eslint-disable-next-line no-mixed-operators
                - (travelFromWidth ? durationTravelFromInMinutes : 0)
                // eslint-disable-next-line no-mixed-operators
                + leftMinutes) *
                widthDimensionPx);
          }
        }
        x = snapPositionX;
      }
    }

    const transform = `translate(${x - left}px, ${y - top}px)`;
    return {
      ...extraStyles,
      transform,
      WebkitTransform: transform,
    };
  };

  if (!isDragging) {
    return null;
  }

  return (
    <DragLayerWrapper isDragging={isDragging}>
      <div style={dragStyle(currentOffset)}>{renderItem()}</div>
    </DragLayerWrapper>
  );
};

DndDragLayer.propTypes = {
  top: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  ganttDimensions: PropTypes.object.isRequired,
  viewDimensions: PropTypes.object.isRequired,
  getDimensions: PropTypes.func.isRequired,
  selectedIds: PropTypes.array,
  view: PropTypes.object,
  dateReference: PropTypes.object,
};

export default DndDragLayer;
